import { Component, computed, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ToastService } from '../../ui';
import { MatDialog } from '@angular/material/dialog';
import { SessionService } from '../../core/session/session.service';
import { AuthService } from '../../core/auth/auth.service';
import { Contract, ContractsService } from './contracts.service';
import { ContractPanelComponent } from './contract-panel.component';
import { CancelCustomerDialogComponent, CancelCustomerDialogData } from './dialogs';
import {
  HlmBtnDirective, HlmIconComponent, HlmInputDirective, HlmSpinnerComponent,
} from '../../ui';
import { environment } from '../../../environments/environment';
interface PhoneServiceMapping {
  phoneServiceId: string;
  contractId: string;
  userAccountId: number;
}
interface EsPhoneServiceHit {
  _source: { phoneServiceId: string };
}

@Component({
  selector: 'cmt-contracts',
  standalone: true,
  imports: [
    FormsModule, ContractPanelComponent,
    HlmBtnDirective, HlmIconComponent, HlmInputDirective, HlmSpinnerComponent,
  ],
  templateUrl: './contracts.component.html',
})
export class ContractsComponent implements OnInit {
  readonly session = inject(SessionService);
  private readonly contractsService = inject(ContractsService);
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(MatDialog);
  private readonly toast = inject(ToastService);
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly contracts = signal<Contract[]>([]);
  readonly loading = signal(true);
  readonly syncing = signal(false);
  readonly searchText = signal('');
  readonly searching = signal(false);
  /** Contract to auto-expand and scroll to (passed via ?contractId= query param). */
  readonly highlightContractId = signal<number | null>(null);
  private loaded = false;
  private readonly pendingAction = signal<string | null>(null);

  private psToContractMap = new Map<string, string>();
  private mappingLoaded = false;
  private esMatchedContractIds = signal<Set<string>>(new Set());
  private readonly searchSubject = new Subject<string>();

  readonly filteredContracts = computed(() => {
    const text = this.searchText().trim().toLowerCase();
    const all = this.contracts();
    if (!text) return all;
    const esMatches = this.esMatchedContractIds();
    return all.filter((c) => String(c.id).includes(text) || esMatches.has(String(c.id)));
  });

  constructor() {
    effect(() => {
      const customerId = this.session.customerId();
      if (customerId !== null && !this.loaded) {
        this.loaded = true;
        this.loadContracts();
      }
    });

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((text) => {
      if (text.length < 2) {
        this.esMatchedContractIds.set(new Set());
        this.searching.set(false);
        return;
      }
      this.searchViaElasticsearch(text);
    });

    effect(() => {
      if (!this.loading() && this.pendingAction()) {
        const action = this.pendingAction();
        this.pendingAction.set(null);
        if (action === 'syncExtensions') this.syncExtensions();
      }
    });
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const action = params['action'];
      const contractId = params['contractId'];
      if (!action && !contractId) return;
      this.router.navigate([], { queryParams: {}, replaceUrl: true });
      if (action === 'syncExtensions') this.pendingAction.set('syncExtensions');
      if (contractId) {
        // Defensive validation — bookmarks/links may carry malformed values.
        // Only positive integers can match a real Contract.id; everything else
        // (NaN, Infinity, decimals, negatives) is silently dropped.
        const n = Number(contractId);
        if (Number.isInteger(n) && n > 0) this.highlightContractId.set(n);
      }
    });
  }

  onSearchChange(text: string): void {
    this.searchText.set(text);
    if (text.trim().length >= 2) this.searching.set(true);
    this.searchSubject.next(text.trim());
  }

  clearSearch(): void {
    this.searchText.set('');
    this.esMatchedContractIds.set(new Set());
    this.searching.set(false);
  }

  loadContracts(): void {
    const customerId = this.session.customerId();
    if (customerId === null) return;
    this.loading.set(true);
    this.contractsService.getContracts(customerId).subscribe({
      next: (contracts) => {
        this.contracts.set(this.sortContracts(contracts));
        this.loading.set(false);
        this.loadPhoneServiceMapping(customerId);
      },
      error: () => {
        this.contracts.set([]);
        this.loading.set(false);
        this.toast.open('Failed to load contracts', 'OK', { duration: 5000 });
      },
    });
  }

  syncExtensions(): void {
    const customerId = this.session.customerId();
    if (customerId === null) return;
    this.syncing.set(true);
    this.contractsService.syncExtensions(customerId).subscribe({
      next: () => {
        this.syncing.set(false);
        this.toast.open('Extensions synchronized', 'OK', { duration: 3000 });
        this.loadContracts();
      },
      error: () => {
        this.syncing.set(false);
        this.toast.open('Failed to sync extensions', 'OK', { duration: 5000 });
      },
    });
  }

  cancelCustomer(): void {
    const customer = this.session.customer();
    if (!customer) return;
    const mainContract = this.contracts().find((c) => c.mainContract) ?? null;
    const data: CancelCustomerDialogData = {
      customerId: customer.id,
      invoiceDay: customer.invoiceDay,
      // Old CMT bounds the cancellation date by the customer's start date and the
      // Send-to-Basic date by the main contract's change rules (EB-4174 follow-up).
      customerStartDate: customer.startDate ?? null,
      mainContractId: mainContract?.id ?? null,
      mainContractStartDate: mainContract?.startDate ?? null,
      mainContractReactivatedStartDate: mainContract?.reactivatedStartDate ?? null,
      mainContractPlanLevel: mainContract?.plan?.level ?? null,
    };
    this.dialog.open(CancelCustomerDialogComponent, { data, width: '700px', maxWidth: '95vw' })
      .afterClosed().subscribe((result) => {
        if (result) {
          this.toast.open('Customer cancelled', 'OK', { duration: 3000 });
          this.session.reloadCustomer();
          this.loadContracts();
        }
      });
  }

  /**
   * Reactivate a cancelled customer. Old-CMT parity (contracts.dir.js reactivateCustomer):
   * reactivation is handled in Manage Services (SMT), not a direct API call — navigate there.
   * The header only surfaces this when the customer has an endDate (i.e. is cancelled).
   */
  reactivateCustomer(): void {
    const customer = this.session.customer();
    if (!customer) return;
    this.router.navigate(['/', customer.id, 'smt']);
  }

  private loadPhoneServiceMapping(customerId: number): void {
    if (this.mappingLoaded) return;
    this.http.get<PhoneServiceMapping[]>(`${environment.phoneServiceUrl}/phoneservices`, {
      params: { customerId: customerId.toString() },
    }).pipe(catchError(() => of([]))).subscribe((services: any[]) => {
      this.psToContractMap.clear();
      for (const svc of services) {
        this.psToContractMap.set(String(svc.id), String(svc.contractId));
      }
      this.mappingLoaded = true;
    });
  }

  private searchViaElasticsearch(text: string): void {
    const customerId = this.session.customerId();
    if (customerId === null) return;
    const isPartialNumber = /^\d{3,}$/.test(text);
    const escapedQuery = text.replace(/\+/g, '\\+');
    const prefixQuery = escapedQuery.endsWith('*') ? escapedQuery : `${escapedQuery}*`;
    const searchClauses: any[] = [{
      simple_query_string: {
        query: prefixQuery,
        fields: ['fullName^3', 'firstName^2', 'lastName^2', 'callcenterNumbers', 'phoneServiceId', 'userAccounts'],
        lenient: true, default_operator: 'and',
      },
    }];
    if (isPartialNumber) {
      searchClauses.push({ wildcard: { callcenterNumbers: { value: `*${text}*` } } });
      searchClauses.push({ wildcard: { phoneServiceId: { value: `*${text}*` } } });
    }
    const body = {
      query: {
        bool: {
          must: [{ term: { customerId } }, { term: { _type: 'phoneservice' } }],
          should: searchClauses, minimum_should_match: 1,
        },
      },
      _source: ['phoneServiceId'], size: 100,
    };
    this.http.post<{ hits: { hits: EsPhoneServiceHit[] } }>(
      `${environment.searchUrl}/search`, body,
    ).pipe(catchError(() => of({ hits: { hits: [] } }))).subscribe((response) => {
      const matched = new Set<string>();
      for (const hit of response.hits.hits) {
        const psId = hit._source.phoneServiceId;
        const contractId = this.psToContractMap.get(psId);
        if (contractId) matched.add(contractId);
      }
      this.esMatchedContractIds.set(matched);
      this.searching.set(false);
    });
  }

  private sortContracts(contracts: Contract[]): Contract[] {
    return [...contracts].sort(
      (a, b) =>
        (b.mainContract ? 1 : 0) - (a.mainContract ? 1 : 0) ||
        a.startDate.localeCompare(b.startDate),
    );
  }
}
